import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { API } from './constants';

export function useContratHandlers({ user, modeSimulateur, navigate, setLoading, resetForm }) {
  const [referenceRecherche, setReferenceRecherche] = useState('');
  const [contratParent, setContratParent] = useState(null);
  const [loadingContrat, setLoadingContrat] = useState(false);
  const [contratsDisponibles, setContratsDisponibles] = useState([]);
  const [loadingContrats, setLoadingContrats] = useState(false);
  const [erreurContrat, setErreurContrat] = useState('');

  // Charger les contrats valides du membre choisi pour le mode avenant
  const membreChoisi = (() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('membre_id') || p.get('membre') || '';
  })();
  const siretChoisi = new URLSearchParams(window.location.search).get('siret') || '';
  useEffect(() => {
    if (modeSimulateur === 'avenant' && user) {
      fetchContratsDisponibles();
    }
  }, [modeSimulateur, user, membreChoisi]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContratsDisponibles = async () => {
    setLoadingContrats(true);
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const r = await axios.get(`${API}/souscriptions/contrats-parents`, { headers, params: { membre_id: membreChoisi, siret: siretChoisi } });
      setContratsDisponibles(r.data.contrats || []);
    } catch {
      setContratsDisponibles([]);
    } finally {
      setLoadingContrats(false);
    }
  };

  const handleRechercherContrat = async () => {
    if (!referenceRecherche.trim()) {
      setErreurContrat('Veuillez saisir une référence de contrat');
      return;
    }
    setLoadingContrat(true);
    setErreurContrat('');
    setContratParent(null);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/souscriptions/search-by-reference/${encodeURIComponent(referenceRecherche.trim())}`,
        { headers }
      );
      const contrat = response.data.souscription;
      const dateFin = contrat.date_fin || contrat.periode_adhesion_fin;
      if (dateFin) {
        const dateFinParsed = new Date(dateFin);
        if (dateFinParsed < new Date()) {
          setErreurContrat(`Ce contrat a expiré le ${dateFinParsed.toLocaleDateString('fr-FR')}. Impossible de créer un avenant sur un contrat expiré.`);
          return;
        }
      }
      if (contrat.statut !== 'validee' && !contrat.paiement_valide) {
        setErreurContrat('Un avenant ne peut être créé que sur une souscription validée et payée.');
        return;
      }
      if (contrat.type_document === 'avenant') {
        setErreurContrat('Impossible de créer un avenant sur un avenant existant. Utilisez la référence du contrat principal.');
        return;
      }
      setContratParent(contrat);
      toast.success('Contrat trouvé ! Vous pouvez maintenant créer un avenant.');
    } catch (error) {
      if (error.response?.status === 404) {
        setErreurContrat('Aucun contrat trouvé avec cette référence');
      } else if (error.response?.status === 403) {
        setErreurContrat('Vous n\'avez pas les droits pour accéder à ce contrat');
      } else {
        setErreurContrat(error.response?.data?.detail || 'Erreur lors de la recherche');
      }
    } finally {
      setLoadingContrat(false);
    }
  };

  const handleSelectContrat = async (contratId) => {
    const choisi = contratsDisponibles.find(c => c.id === contratId || c.numero_police === contratId);
    if (!choisi) return;
    let contrat = choisi;
    try {
      // Données à jour (date de fin + CA réels) depuis le contrat parent
      const r = await axios.get(`${API}/souscriptions/search-by-reference/${encodeURIComponent(choisi.id || choisi.numero_police)}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      contrat = { ...choisi, ...r.data.souscription };
    } catch { /* garde la version de la liste */ }
    setContratParent(contrat);
    setReferenceRecherche(contrat.numero_police || contrat.ref_solidaire || '');
    setErreurContrat('');
    toast.success('Contrat sélectionné ! Vous pouvez maintenant créer un avenant.');
  };

  const handleProcederAuPaiement = async () => {
    if (!contratParent) {
      toast.error('Veuillez d\'abord sélectionner un contrat parent');
      return;
    }
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const avenantData = {
        montant_ht_contribution: 0,
        motif: 'Ajout d\'activités supplémentaires',
        notes: 'Avenant créé depuis le simulateur',
        activites_selectionnees: {}
      };
      const contratId = contratParent.id || contratParent.numero_police;
      const response = await axios.post(
        `${API}/souscriptions/admin/${encodeURIComponent(contratId)}/avenant`,
        avenantData, { headers }
      );
      if (response.data.success) {
        const avenantRef = response.data.avenant.reference;
        const avenantId = response.data.avenant.id;
        toast.success(`Avenant ${avenantRef} créé avec succès !`);
        const userRole = user?.role?.toLowerCase();
        if (userRole === 'member' || userRole === 'membre') {
          navigate('/member/dashboard', {
            state: { message: `Avenant ${avenantRef} créé.`, avenantId, showAvenants: true }
          });
        } else {
          navigate('/admin/super-dashboard', {
            state: { message: `Avenant ${avenantRef} créé avec succès.`, avenantId, tab: 'souscriptions' }
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'avenant');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAvenant = () => {
    setContratParent(null);
    setReferenceRecherche('');
    setErreurContrat('');
    if (resetForm) resetForm();
  };

  return {
    referenceRecherche, setReferenceRecherche,
    contratParent, setContratParent,
    loadingContrat,
    contratsDisponibles, loadingContrats,
    erreurContrat, setErreurContrat,
    fetchContratsDisponibles,
    handleRechercherContrat,
    handleSelectContrat,
    handleProcederAuPaiement,
    handleResetAvenant
  };
}
